import * as React from 'react';
import { Link } from 'react-router-dom';

import { DataTable, type IDataColumn } from '@/design';
import { useCan } from '@/domains/auth';
import type { IRankingTerritoryPlacement } from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { personHubPath } from '../../components/participant-link';
import { RankingFlags } from '../../components/ranking-flags';
import { rankingPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { useRanking } from './ranking';

/** The flagged placements' read: survey-api's `GET …/placements?flagged=true`, every flagged row
 *  of the ranking by territory then place, read again with the record and after each edit (the
 *  ranking's version) — none asked for where `wanted` is false. */
export function useFlaggedPlacements(wanted: boolean): IFlaggedRead {
  const record = useRanking();
  const { data, error } = useAdminData<IRankingTerritoryPlacement[]>(
    async (signal) => {
      if (!wanted) return [];
      try {
        return await rankingProcessApi.flaggedPlacements(record.key, signal);
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the flagged placements'));
      }
    },
    [wanted, record.freshness, record.overview?.ranking.version]
  );
  return { data, error };
}

export interface IFlaggedRead {
  data: Nullable<IRankingTerritoryPlacement[]>;
  error: Nullable<string>;
}

/**
 * A territorial ranking's flagged placements (SR-506), in the place of a territory's table while
 * the territory list's "Flagged" is chosen. A row names its territory, which opens that
 * territory's table — where Move and Remove are — and says each flag's sentence in full.
 */
export function FlaggedPlacements({ read }: { read: IFlaggedRead }) {
  const record = useRanking();
  const browses = useCan()('responses.browse');
  const { data, error } = read;

  const columns: IDataColumn<IRankingTerritoryPlacement>[] = [
    {
      id: 'territory',
      header: 'Territory',
      cell: (row) => (
        <Link
          className="hover:underline"
          to={rankingPath(
            record.key,
            'placements',
            `?territory=${encodeURIComponent(row.territory ?? '')}`
          )}
        >
          {row.territory}
        </Link>
      )
    },
    { id: 'place', header: '#', numeric: true, cell: (row) => row.place },
    {
      id: 'name',
      header: 'Participant',
      cell: (row) =>
        browses ? (
          <Link to={personHubPath(row.participantId)} className="font-medium hover:underline">
            {row.displayName}
          </Link>
        ) : (
          <span className="font-medium">{row.displayName}</span>
        )
    },
    {
      id: 'flags',
      header: 'Flags',
      cell: (row) => (
        <span className="grid gap-1">
          {(row.flags ?? []).map((flag) => (
            <span key={flag.code} className="flex flex-wrap items-start gap-1.5 text-sm">
              <RankingFlags flags={[flag]} said />
              <span className="pt-1">{flag.message}</span>
            </span>
          ))}
        </span>
      )
    }
  ];

  return (
    <section aria-label="Flagged placements" className="grid gap-3">
      <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">
        Flagged placements
        {data && <span className="ml-2 font-normal text-muted-foreground">· {data.length}</span>}
      </h2>
      <DataTable
        label="Flagged placements"
        columns={columns}
        rows={data && data.length > 0 ? data : null}
        rowKey={(row) => row.participantId}
        rowProps={(row) =>
          ({ 'data-participant': row.participantId }) as React.HTMLAttributes<HTMLTableRowElement>
        }
        maxHeight="none"
        state={
          data === null
            ? error
              ? { kind: 'error', content: <LoadError message={error} /> }
              : { kind: 'loading' }
            : { kind: 'empty', content: 'No placement of this ranking is flagged.' }
        }
      />
    </section>
  );
}
